import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Loader2, Check, X, Briefcase, ArrowRight, Heart, Coffee, Minus, Equal, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ToneOption, SizeOption } from '@/lib/hooks/use-ai-writing-assistant';

interface AIAssistantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (
    currentText: string | undefined,
    options: {
      customInstructions: string;
      tone?: ToneOption;
      size?: SizeOption;
      iterationPrompt?: string;
      previousGeneratedText?: string;
    }
  ) => Promise<string | null>;
  onAccept: (text: string) => void;
  generatedText: string | null;
  isLoading: boolean;
  type: 'headline' | 'bio' | 'client_bio' | 'project_description';
  currentText?: string;
}

const TONE_OPTIONS: ToneOption[] = ['professional', 'direct', 'friendly', 'casual'];
const SIZE_OPTIONS: SizeOption[] = ['concise', 'moderate', 'elaborate'];

const TONE_LABELS: Record<ToneOption, string> = {
  professional: 'Professional',
  direct: 'Direct',
  friendly: 'Friendly',
  casual: 'Casual',
};

const SIZE_LABELS: Record<SizeOption, string> = {
  concise: 'Concise',
  moderate: 'Moderate',
  elaborate: 'Elaborate',
  detailed: 'Detailed',
};

const TONE_ICONS: Record<ToneOption, React.ElementType> = {
  professional: Briefcase,
  direct: ArrowRight,
  friendly: Heart,
  casual: Coffee,
};

const SIZE_ICONS: Record<SizeOption, React.ElementType> = {
  concise: Minus,
  moderate: Equal,
  elaborate: Plus,
  detailed: Plus,
};

export function AIAssistantDialog({
  open,
  onOpenChange,
  onGenerate,
  onAccept,
  generatedText,
  isLoading,
  type,
  currentText,
}: AIAssistantDialogProps) {
  const [customInstructions, setCustomInstructions] = useState('');
  const [selectedTone, setSelectedTone] = useState<ToneOption>('professional');
  const [selectedSize, setSelectedSize] = useState<SizeOption>('moderate');
  const [iterationPrompt, setIterationPrompt] = useState('');
  const [editableGeneratedText, setEditableGeneratedText] = useState<string>('');
  const showPreview = !!generatedText;

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setIterationPrompt('');
      setCustomInstructions('');
      setSelectedTone('professional');
      setSelectedSize('moderate');
      setEditableGeneratedText('');
    }
  }, [open]);

  // Sync local editable text when a new generation arrives
  useEffect(() => {
    if (generatedText) {
      setEditableGeneratedText(generatedText);
    }
  }, [generatedText]);

  const handleInitialGenerate = async () => {
    if (!customInstructions.trim()) {
      return; // Instructions are mandatory
    }
    
    // For initial generation, use the current text from the field (if any)
    await onGenerate(currentText, {
      customInstructions: customInstructions.trim(),
      tone: selectedTone,
      size: selectedSize,
    });
    
    // The dialog will show preview when generatedText prop updates
  };

  const handleIterate = async () => {
    if (!iterationPrompt.trim() || !editableGeneratedText.trim()) {
      return;
    }
    
    // For iterations, treat the latest (possibly edited) text as the current text context
    await onGenerate(editableGeneratedText || currentText, {
      customInstructions: customInstructions.trim(),
      tone: selectedTone,
      size: selectedSize,
      iterationPrompt: iterationPrompt.trim(),
      previousGeneratedText: editableGeneratedText,
    });
    
    setIterationPrompt('');
  };

  const handleAccept = () => {
    if (editableGeneratedText.trim()) {
      onAccept(editableGeneratedText.trim());
      // Reset all state before closing
      setIterationPrompt('');
      setCustomInstructions('');
      setSelectedTone('professional');
      setSelectedSize('moderate');
      onOpenChange(false);
    }
  };

  const handleDiscard = () => {
    setIterationPrompt('');
    setCustomInstructions('');
    setSelectedTone('professional');
    setSelectedSize('moderate');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#4669ed]" />
            AI Writing Assistant
          </DialogTitle>
          <DialogDescription>
            {showPreview 
              ? 'Review the generated content and iterate if needed'
              : `Customize how your ${type === 'headline' ? 'headline' : type === 'client_bio' ? 'company bio' : type === 'project_description' ? 'project description' : 'bio'} is generated`
            }
          </DialogDescription>
        </DialogHeader>

        {!showPreview ? (
          // Initial generation form
          <div className="space-y-4 py-4">
            {/* Custom Instructions - Mandatory */}
            <div className="space-y-2">
              <Label htmlFor="instructions" className="text-sm font-semibold text-slate-700">
                Instructions <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="instructions"
                value={customInstructions}
                onChange={(e) => setCustomInstructions(e.target.value)}
                placeholder="e.g., Emphasize my experience with machine learning, mention my startup background, highlight my expertise in NLP..."
                className="min-h-[100px] resize-none"
                maxLength={500}
                required
              />
              <p className="text-xs text-slate-400">{customInstructions.length}/500 characters</p>
            </div>

            {/* Tone and Length Selection inline */}
            <div className="flex flex-wrap items-center gap-4">
              {/* Tone Selection */}
              <div className="flex items-center gap-2 flex-wrap">
                {TONE_OPTIONS.map((tone) => {
                  const ToneIcon = TONE_ICONS[tone];
                  return (
                    <Badge
                      key={tone}
                      variant={selectedTone === tone ? 'default' : 'outline'}
                      className={cn(
                        "cursor-pointer transition-colors rounded-full px-3 py-1.5 text-sm flex items-center gap-1.5",
                        selectedTone === tone
                          ? 'bg-[#4669ed] text-white border-transparent hover:bg-[#2b42a3]'
                          : 'hover:bg-slate-100 border-slate-200'
                      )}
                      onClick={() => setSelectedTone(tone)}
                    >
                      <ToneIcon className="h-3.5 w-3.5" />
                      {TONE_LABELS[tone]}
                    </Badge>
                  );
                })}
              </div>

              {/* Size/Length Selection */}
              <div className="flex items-center gap-2 flex-wrap">
                {SIZE_OPTIONS.map((size) => {
                  const SizeIcon = SIZE_ICONS[size];
                  return (
                    <Badge
                      key={size}
                      variant={selectedSize === size ? 'default' : 'outline'}
                      className={cn(
                        "cursor-pointer transition-colors rounded-full px-3 py-1.5 text-sm flex items-center gap-1.5",
                        selectedSize === size
                          ? 'bg-[#4669ed] text-white border-transparent hover:bg-[#2b42a3]'
                          : 'hover:bg-slate-100 border-slate-200'
                      )}
                      onClick={() => setSelectedSize(size)}
                    >
                      <SizeIcon className="h-3.5 w-3.5" />
                      {SIZE_LABELS[size]}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          // Preview and iteration section
          <div className="space-y-6 py-4">
            {/* Generated Content Preview */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#4669ed]" />
                  Generated Content
                </Label>
              </div>
              <div className="rounded-xl border-2 border-[#4669ed]/30 bg-[#4669ed]/5 p-4">
                <Textarea
                  value={editableGeneratedText}
                  onChange={(e) => setEditableGeneratedText(e.target.value)}
                  className="text-slate-700 leading-relaxed bg-white rounded-lg border border-slate-200 max-h-[300px] overflow-y-auto min-h-[120px] resize-y"
                />
              </div>
            </div>

            {/* Iteration Prompt */}
            <div className="space-y-2">
              <Label htmlFor="iteration" className="text-sm font-semibold text-slate-700">
                Refine the content (Optional)
              </Label>
              <Textarea
                id="iteration"
                value={iterationPrompt}
                onChange={(e) => setIterationPrompt(e.target.value)}
                placeholder="e.g., Make it more technical, add more details about my experience, make it shorter..."
                className="min-h-[80px] resize-none"
                maxLength={300}
              />
              <div className='flex justify-between'>
                <p className="text-xs text-slate-400">{iterationPrompt.length}/300 characters</p>
                {/* Regenerate button under the iteration input */}
                <Button
                  variant="outline"
                  onClick={handleIterate}
                  disabled={isLoading || !iterationPrompt.trim()}
                  className="rounded-[40px] border-[#4669ed] text-[#4669ed] hover:bg-[#4669ed]/10"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Regenerating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Regenerate
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {!showPreview ? (
            // Initial generation buttons
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
                className="rounded-[40px] border-slate-200 text-slate-900"
              >
                Cancel
              </Button>
              <Button
                onClick={handleInitialGenerate}
                disabled={isLoading || !customInstructions.trim()}
                className="rounded-[40px] bg-[#4669ed] hover:bg-[#2b42a3] text-white"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate
                  </>
                )}
              </Button>
            </>
          ) : (
            // Preview/iteration buttons (Regenerate is now in the content area)
            <>
              <Button
                variant="outline"
                onClick={handleDiscard}
                disabled={isLoading}
                className="rounded-[40px] border-slate-200 text-slate-900"
              >
                <X className="h-4 w-4 mr-2" />
                Discard
              </Button>
              <Button
                onClick={handleAccept}
                disabled={isLoading}
                className="rounded-[40px] bg-[#4669ed] hover:bg-[#2b42a3] text-white"
              >
                <Check className="h-4 w-4 mr-2" />
                Accept & Use
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

